/**
 * Centralized clipboard utility module
 * Handles clipboard operations across web, Electron, and React Native environments
 */

// Check if running in Electron
const isElectron = () => {
  const hasWindow = typeof window !== "undefined";
  const hasElectronAPI = hasWindow && window.electronAPI !== undefined;
  const hasClipboardWrite = hasElectronAPI && typeof window.electronAPI.clipboardWriteText === "function";
  return hasClipboardWrite;
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy to clipboard
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
const copyToClipboard = async text => {
  if (!text) {
    console.error("[Clipboard] No text provided");
    return false;
  }

  // Ensure text is a string
  const textToCopy = String(text);
  try {
    // Try Electron API first - check if we're in Electron environment
    if (isElectron()) {
      try {
        const result = await window.electronAPI.clipboardWriteText(textToCopy);
        if (result?.success === true) {
          return true;
        }
      } catch (electronError) {
        console.error("[Clipboard] Electron API error:", electronError);
      }
    }

    // Fall back to navigator.clipboard for modern web browsers
    if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(textToCopy);
      return true;
    }

    // Final fallback: use DOM method for older browsers
    return fallbackCopy(textToCopy);
  } catch (error) {
    console.error("[Clipboard] Failed to copy to clipboard:", error);
    // Try fallback as last resort
    return fallbackCopy(textToCopy);
  }
};

/**
 * Fallback copy method using DOM manipulation
 * @param {string} text - Text to copy
 * @returns {boolean} - Returns true if successful, false otherwise
 */
const fallbackCopy = text => {
  try {
    // Check if document is available
    if (typeof document === "undefined") {
      console.error("[Clipboard] Fallback copy failed: document is not available");
      return false;
    }

    // Create temporary textarea element
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    textarea.setAttribute("readonly", "");
    document.body.appendChild(textarea);

    // Select and copy
    textarea.focus();
    textarea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textarea);
    return successful;
  } catch (error) {
    console.error("[Clipboard] Fallback copy failed:", error);
    return false;
  }
};

/**
 * Check if Electron clipboard read API is available
 */
const hasElectronClipboardRead = () => {
  const hasWindow = typeof window !== "undefined";
  const hasElectronAPI = hasWindow && window.electronAPI !== undefined;
  const hasClipboardRead = hasElectronAPI && typeof window.electronAPI.clipboardReadText === "function";
  return hasClipboardRead;
};

/**
 * Read text from clipboard
 * @returns {Promise<string|null>} - Returns clipboard text or null if failed
 */
const readFromClipboard = async () => {
  try {
    // Try Electron API first
    if (hasElectronClipboardRead()) {
      const result = await window.electronAPI.clipboardReadText();
      if (result?.success) {
        return result.text;
      }
      return null;
    }

    // Fall back to navigator.clipboard for modern web browsers
    if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.readText) {
      const text = await navigator.clipboard.readText();
      return text;
    }
    console.warn("[Clipboard] No clipboard read API available");
    return null;
  } catch (error) {
    console.error("[Clipboard] Failed to read from clipboard:", error);
    return null;
  }
};
var clipboard = {
  copyToClipboard,
  readFromClipboard,
  isElectron
};

export { clipboard as a, copyToClipboard as c, readFromClipboard as r };
